import { PageSpeedResult } from '../types';
import { getImportantOpportunities } from './dataTransformers';
import { getRatingGrade, getRatingColor } from './overallRating';
import { translateOpportunityTitle, translateDescription } from './reportGenerators';

/**
 * V2 디자인 시스템 (AppV2.tsx와 완전 동일)
 */
const designSystem = {
  colors: {
    primary: '#2467E7',
    primaryDark: '#1B4DB8',
    primaryLight: '#E8F2FF',
    secondary: '#6C5CE7',
    accent: '#00D4FF',
    success: '#00B894',
    warning: '#FDCB6E',
    danger: '#E17055',
    bgPrimary: '#FAFBFC',
    bgSecondary: '#FFFFFF',
    textPrimary: '#1A202C',
    textSecondary: '#718096',
    textMuted: '#A0AEC0',
    border: '#E2E8F0'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.12)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.15)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.2)'
  }
};

/**
 * V2 스타일 HTML 보고서 생성 (AppV2.tsx 디자인 완전 계승)
 */
export const generateHtmlReportV2 = (
  pageSpeedData: PageSpeedResult,
  url: string,
  llmAnalysis?: string | null,
  customRatings?: { desktop: number; mobile: number; overall: number }
): string => {
  if (!pageSpeedData) return '';

  // 용량 포맷팅 함수
  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 KB';
    return bytes > 1024 * 1024
      ? `${(bytes / 1024 / 1024).toFixed(2)} MB`
      : `${(bytes / 1024).toFixed(0)} KB`;
  };

  // 성능 점수 및 등급
  const desktopScore = customRatings?.desktop || 0;
  const mobileScore = customRatings?.mobile || 0;
  const desktopGrade = desktopScore > 0 ? getRatingGrade(desktopScore) : '-';
  const mobileGrade = mobileScore > 0 ? getRatingGrade(mobileScore) : '-';
  const desktopColor = desktopScore > 0 ? getRatingColor(desktopScore) : designSystem.colors.textSecondary;
  const mobileColor = mobileScore > 0 ? getRatingColor(mobileScore) : designSystem.colors.textSecondary;

  // 개선 권장사항 추출
  const desktopOpportunities = pageSpeedData.desktop?.opportunities
    ? getImportantOpportunities(pageSpeedData.desktop.opportunities)
    : [];
  const mobileOpportunities = pageSpeedData.mobile?.opportunities
    ? getImportantOpportunities(pageSpeedData.mobile.opportunities)
    : [];

  // 마크다운 특수문자 제거 함수
  const cleanMarkdownText = (text: string): string => {
    return text
      .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/^[-*•]\s+/, '')
      .replace(/^\d+\.\s+/, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // LLM 분석 파싱 (화면과 동일한 로직)
  const parseAnalysisContent = (analysis: string) => {
    if (!analysis || typeof analysis !== 'string') {
      return { goodPoints: [], improvementAreas: [] };
    }

    const lines = analysis.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const goodPoints: string[] = [];
    const improvementAreas: Array<{title: string, content: string[]}> = [];

    let currentSection = 'none';
    let currentCategory = '';
    let currentCategoryContent: string[] = [];

    for (const line of lines) {
      // 메인 섹션 구분자 감지
      if (line.includes('✅') && (line.includes('우수한 점') || line.includes('우수한'))) {
        if (currentSection === 'improvement' && currentCategory && currentCategoryContent.length > 0) {
          improvementAreas.push({
            title: cleanMarkdownText(currentCategory),
            content: currentCategoryContent.map(l => cleanMarkdownText(l)).filter(l => l.length > 0)
          });
        }
        currentSection = 'good';
        currentCategory = '';
        currentCategoryContent = [];
        continue;
      } else if (line.includes('⚠️') && (line.includes('개선이 필요한') || line.includes('개선'))) {
        if (currentSection === 'good' && currentCategoryContent.length > 0) {
          goodPoints.push(...currentCategoryContent);
          currentCategoryContent = [];
        }
        currentSection = 'improvement';
        currentCategory = '';
        currentCategoryContent = [];
        continue;
      }

      // 볼드 카테고리 헤더 감지
      const boldHeaderMatch = line.match(/^\*\*(.+?)\*\*:?$/);
      if (boldHeaderMatch && currentSection === 'improvement') {
        if (currentCategory && currentCategoryContent.length > 0) {
          improvementAreas.push({
            title: cleanMarkdownText(currentCategory),
            content: currentCategoryContent.map(l => cleanMarkdownText(l)).filter(l => l.length > 0)
          });
        }
        currentCategory = boldHeaderMatch[1];
        currentCategoryContent = [];
        continue;
      }

      // 헤더 스킵
      if (line.match(/^#{1,6}\s/) || line.match(/^[\d]+\.\s*$/) || line.match(/^-\s*$/)) {
        continue;
      }

      // 실제 내용 수집
      if (line.length > 0) {
        currentCategoryContent.push(line);
      }
    }

    // 마지막 섹션 정리
    if (currentSection === 'good' && currentCategoryContent.length > 0) {
      goodPoints.push(...currentCategoryContent);
    } else if (currentSection === 'improvement' && currentCategory && currentCategoryContent.length > 0) {
      improvementAreas.push({
        title: cleanMarkdownText(currentCategory),
        content: currentCategoryContent.map(l => cleanMarkdownText(l)).filter(l => l.length > 0)
      });
    }

    return {
      goodPoints: goodPoints.map(p => cleanMarkdownText(p)).filter(p => p.length > 0),
      improvementAreas
    };
  };

  // 파싱된 데이터를 HTML로 변환
  const generateLlmHtml = (analysis: string) => {
    if (!analysis) return '';

    const { goodPoints, improvementAreas } = parseAnalysisContent(analysis);
    let html = '';

    // 우수한 점 섹션
    if (goodPoints.length > 0) {
      html += `
        <div style="
          margin-bottom: 2rem;
          padding: 2.5rem;
          background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
          border-radius: 1.5rem;
          border: 2px solid rgba(0, 184, 148, 0.3);
          box-shadow: 0 8px 20px rgba(0, 184, 148, 0.1);
        ">
          <h3 style="
            font-size: 1.75rem;
            font-weight: 700;
            color: #00B894;
            margin: 0 0 1.75rem 0;
            display: flex;
            align-items: center;
            gap: 0.75rem;
          ">
            <span style="
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 2.5rem;
              height: 2.5rem;
              border-radius: 50%;
              background: linear-gradient(135deg, #00B894 0%, #00d4aa 100%);
              color: white;
              font-size: 1.25rem;
            ">✓</span>
            우수한 점
          </h3>
          <div style="
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 1.25rem;
          ">
            ${goodPoints.map(point => `
              <div style="
                padding: 1.25rem 1.5rem;
                border-radius: 1.25rem;
                background: linear-gradient(135deg, rgba(0, 184, 148, 0.08) 0%, rgba(0, 212, 170, 0.12) 100%);
                display: flex;
                align-items: flex-start;
                gap: 0.75rem;
                border: 2px solid rgba(0, 184, 148, 0.2);
              ">
                <span style="
                  color: #00B894;
                  font-weight: 800;
                  font-size: 1.5rem;
                  line-height: 1;
                  flex-shrink: 0;
                ">✓</span>
                <span style="
                  color: #1A202C;
                  line-height: 1.7;
                  font-size: 0.95rem;
                  font-weight: 500;
                ">${point}</span>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    // 개선이 필요한 영역 섹션
    if (improvementAreas.length > 0) {
      html += `
        <div style="
          margin-bottom: 2rem;
          padding: 2.5rem;
          background: linear-gradient(135deg, #ffffff 0%, #fffbeb 100%);
          border-radius: 1.5rem;
          border: 2px solid rgba(253, 203, 110, 0.3);
          box-shadow: 0 8px 20px rgba(253, 203, 110, 0.1);
        ">
          <h3 style="
            font-size: 1.75rem;
            font-weight: 700;
            color: #FDCB6E;
            margin: 0 0 1.75rem 0;
            display: flex;
            align-items: center;
            gap: 0.75rem;
          ">
            <span style="
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 2.5rem;
              height: 2.5rem;
              border-radius: 50%;
              background: linear-gradient(135deg, #FDCB6E 0%, #fbbf24 100%);
              color: white;
              font-size: 1.25rem;
            ">⚠</span>
            개선이 필요한 영역
          </h3>
          <div style="
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 1.5rem;
          ">
            ${improvementAreas.map(area => `
              <div style="
                padding: 1.5rem;
                background: linear-gradient(135deg, rgba(253, 203, 110, 0.08) 0%, rgba(251, 191, 36, 0.12) 100%);
                border-radius: 1.5rem;
                border: 2px solid rgba(253, 203, 110, 0.2);
              ">
                <h4 style="
                  margin: 0 0 1rem 0;
                  color: #1A202C;
                  font-size: 1.125rem;
                  font-weight: 700;
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                ">
                  <span style="
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 1.75rem;
                    height: 1.75rem;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #FDCB6E 0%, #fbbf24 100%);
                    color: white;
                    font-size: 0.875rem;
                  ">🔧</span>
                  ${area.title}
                </h4>
                <div style="
                  line-height: 1.75;
                  color: #475569;
                  font-size: 0.9rem;
                ">
                  ${area.content.map(line => `
                    <div style="
                      margin-bottom: 0.65rem;
                      padding-left: 1.25rem;
                      position: relative;
                    ">
                      <span style="
                        position: absolute;
                        left: 0;
                        color: #FDCB6E;
                        font-weight: 800;
                        font-size: 1.1rem;
                      ">•</span>
                      <span style="font-weight: 500;">${line}</span>
                    </div>
                  `).join('')}
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    return html;
  };

  const html = `
<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>웹사이트 성능 분석 보고서 V2</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            font-family: 'Inter', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            background: ${designSystem.colors.bgPrimary};
            color: ${designSystem.colors.textPrimary};
            line-height: 1.6;
        }
        .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 24px;
            background: ${designSystem.colors.primary};
            color: white;
            border: none;
            border-radius: 12px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: ${designSystem.shadows.lg};
            z-index: 1000;
            transition: all 0.3s ease;
        }
        .print-button:hover {
            background: ${designSystem.colors.primaryDark};
            transform: translateY(-2px);
            box-shadow: ${designSystem.shadows.xl};
        }
        .hidden-resources {
            display: none;
        }
        .show-more-btn {
            margin-top: 0.75rem;
            padding: 0.625rem 1.25rem;
            background: linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.primaryDark} 100%);
            color: white;
            border: none;
            border-radius: 0.75rem;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 2px 8px rgba(36, 103, 231, 0.3);
        }
        .show-more-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(36, 103, 231, 0.4);
        }
        @media print {
            .print-button {
                display: none;
            }
            .hidden-resources {
                display: block !important;
            }
            .show-more-btn {
                display: none !important;
            }
        }
    </style>
    <script>
        function toggleResources(buttonId, targetId) {
            const button = document.getElementById(buttonId);
            const target = document.getElementById(targetId);

            if (target.style.display === 'none' || target.style.display === '') {
                target.style.display = 'block';
                button.textContent = '접기 ▲';
                button.style.background = 'linear-gradient(135deg, #6C5CE7 0%, #5A4AC7 100%)';
            } else {
                target.style.display = 'none';
                const remaining = target.querySelectorAll('.resource-item').length;
                button.textContent = '나머지 ' + remaining + '개 더보기 ▼';
                button.style.background = 'linear-gradient(135deg, #2467E7 0%, #1B4DB8 100%)';
            }
        }
    </script>
</head>
<body>
    <button class="print-button" onclick="window.print()">📄 인쇄하기</button>

    <!-- 풀와이드 헤더 -->
    <div style="
        background: linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%);
        color: white;
        padding: 3rem 2rem;
        text-align: center;
        position: relative;
        overflow: hidden;
    ">
        <!-- 배경 장식 -->
        <div style="
            position: absolute;
            top: -100px;
            right: -100px;
            width: 200px;
            height: 200px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 50%;
        "></div>
        <div style="
            position: relative;
            z-index: 1;
            max-width: 1200px;
            margin: 0 auto;
        ">
            <h1 style="
                font-size: 2.5rem;
                font-weight: 900;
                margin-bottom: 1rem;
            ">분석 완료</h1>
            <p style="
                font-size: 1.125rem;
                color: rgba(255, 255, 255, 0.9);
                margin: 0;
            ">${url} 분석 결과입니다</p>
            <p style="
                font-size: 0.875rem;
                color: rgba(255, 255, 255, 0.7);
                margin-top: 0.5rem;
            ">생성 시간: ${new Date().toLocaleString('ko-KR')}</p>
        </div>
    </div>

    <!-- 메인 분석 대시보드 -->
    <div style="padding: 4rem 5%;">
        <!-- 4개 핵심 메트릭 카드 그리드 -->
        <div style="
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 2rem;
            margin-bottom: 4rem;
        ">
            <!-- 1. 핵심 지표 (모바일 FCP/LCP) -->
            <div style="
                background: white;
                border-radius: 2rem;
                padding: 2.5rem;
                box-shadow: ${designSystem.shadows.lg};
                border: 1px solid ${designSystem.colors.border};
                text-align: center;
            ">
                <div style="
                    display: inline-block;
                    padding: 0.75rem 1.5rem;
                    border-radius: 1rem;
                    background-color: ${designSystem.colors.primary}20;
                    margin-bottom: 1.5rem;
                ">
                    <h3 style="
                        font-size: 1.875rem;
                        font-weight: 600;
                        color: ${designSystem.colors.primary};
                        margin: 0;
                    ">핵심 지표</h3>
                </div>
                <div style="display: flex; flex-direction: column; gap: 1.5rem;">
                    <!-- FCP -->
                    <div style="
                        padding-bottom: 1.5rem;
                        border-bottom: 1px solid ${designSystem.colors.border};
                    ">
                        <div style="
                            font-size: 2.5rem;
                            font-weight: 700;
                            color: ${designSystem.colors.primary};
                        ">
                            ${pageSpeedData.mobile?.firstContentfulPaint
                              ? Math.round(pageSpeedData.mobile.firstContentfulPaint * 10) / 10
                              : '—'}s
                        </div>
                        <div style="
                            font-size: 0.75rem;
                            font-weight: 500;
                            color: ${designSystem.colors.textSecondary};
                            margin-top: 0.5rem;
                        ">첫 콘텐츠 표시</div>
                    </div>
                    <!-- LCP -->
                    <div>
                        <div style="
                            font-size: 2.5rem;
                            font-weight: 700;
                            color: ${designSystem.colors.primary};
                        ">
                            ${pageSpeedData.mobile?.largestContentfulPaint
                              ? Math.round(pageSpeedData.mobile.largestContentfulPaint * 10) / 10
                              : '—'}s
                        </div>
                        <div style="
                            font-size: 0.75rem;
                            font-weight: 500;
                            color: ${designSystem.colors.textSecondary};
                            margin-top: 0.5rem;
                        ">최대 콘텐츠 표시</div>
                    </div>
                </div>
            </div>

            <!-- 2. 성능 점수 (데스크톱/모바일) -->
            <div style="
                background: white;
                border-radius: 2rem;
                padding: 2.5rem;
                box-shadow: ${designSystem.shadows.lg};
                border: 1px solid ${designSystem.colors.border};
                text-align: center;
            ">
                <div style="
                    display: inline-block;
                    padding: 0.75rem 1.5rem;
                    border-radius: 1rem;
                    background-color: ${desktopScore > 0 ? desktopColor + '20' : designSystem.colors.border};
                    margin-bottom: 1.5rem;
                ">
                    <h3 style="
                        font-size: 1.875rem;
                        font-weight: 600;
                        color: ${desktopScore > 0 ? desktopColor : designSystem.colors.textSecondary};
                        margin: 0;
                    ">성능 점수</h3>
                </div>
                <div style="display: flex; flex-direction: column; gap: 1.5rem;">
                    <!-- 데스크톱 -->
                    <div style="
                        padding-bottom: 1.5rem;
                        border-bottom: 1px solid ${designSystem.colors.border};
                    ">
                        <div style="
                            font-size: 3rem;
                            font-weight: 900;
                            color: ${desktopColor};
                            line-height: 1;
                        ">${desktopScore > 0 ? Math.round(desktopScore) : '준비중'}</div>
                        <div style="
                            font-size: 0.875rem;
                            font-weight: 600;
                            color: ${desktopColor};
                            margin-top: 0.5rem;
                        ">${desktopGrade}</div>
                        <div style="
                            font-size: 0.75rem;
                            color: ${designSystem.colors.textMuted};
                        ">데스크톱</div>
                    </div>
                    <!-- 모바일 -->
                    <div>
                        <div style="
                            font-size: 3rem;
                            font-weight: 900;
                            color: ${mobileColor};
                            line-height: 1;
                        ">${mobileScore > 0 ? Math.round(mobileScore) : '준비중'}</div>
                        <div style="
                            font-size: 0.875rem;
                            font-weight: 600;
                            color: ${mobileColor};
                            margin-top: 0.5rem;
                        ">${mobileGrade}</div>
                        <div style="
                            font-size: 0.75rem;
                            color: ${designSystem.colors.textMuted};
                        ">모바일</div>
                    </div>
                </div>
            </div>

            <!-- 3. 페이지 크기 -->
            <div style="
                background: white;
                border-radius: 2rem;
                padding: 2.5rem;
                box-shadow: ${designSystem.shadows.lg};
                border: 1px solid ${designSystem.colors.border};
                text-align: center;
            ">
                <div style="
                    display: inline-block;
                    padding: 0.75rem 1.5rem;
                    border-radius: 1rem;
                    background-color: ${designSystem.colors.secondary}20;
                    margin-bottom: 1.5rem;
                ">
                    <h3 style="
                        font-size: 1.875rem;
                        font-weight: 600;
                        color: ${designSystem.colors.secondary};
                        margin: 0;
                    ">페이지 크기</h3>
                </div>
                <div style="display: flex; flex-direction: column; gap: 1.5rem;">
                    <!-- 데스크톱 -->
                    <div style="
                        padding-bottom: 1.5rem;
                        border-bottom: 1px solid ${designSystem.colors.border};
                    ">
                        <div style="
                            font-size: 2rem;
                            font-weight: 700;
                            color: ${designSystem.colors.secondary};
                            margin-bottom: 1rem;
                        ">
                            ${formatBytes(pageSpeedData.desktop?.pageSize?.totalSize || 0)}
                            <span style="
                                font-size: 0.875rem;
                                font-weight: 500;
                                color: ${designSystem.colors.textSecondary};
                                margin-left: 0.5rem;
                            ">(데스크톱)</span>
                        </div>
                        <div style="
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            gap: 0.5rem;
                            font-size: 0.875rem;
                        ">
                            <div style="
                                padding: 0.5rem 0.75rem;
                                border-radius: 0.75rem;
                                background-color: ${designSystem.colors.secondary}15;
                            ">
                                <div style="color: ${designSystem.colors.secondary}; font-weight: 600;">리소스:</div>
                                <div style="color: ${designSystem.colors.textPrimary}; font-weight: 600;">${pageSpeedData.desktop?.pageSize?.resourceCount || 0}개</div>
                            </div>
                            <div style="
                                padding: 0.5rem 0.75rem;
                                border-radius: 0.75rem;
                                background-color: ${designSystem.colors.secondary}15;
                            ">
                                <div style="color: ${designSystem.colors.secondary}; font-weight: 600;">CSS:</div>
                                <div style="color: ${designSystem.colors.textPrimary}; font-weight: 600;">${formatBytes(
                                  pageSpeedData.desktop?.pageSize?.resourceBreakdown?.['stylesheet'] ||
                                  pageSpeedData.desktop?.pageSize?.resourceBreakdown?.['Stylesheet'] ||
                                  pageSpeedData.desktop?.pageSize?.resourceBreakdown?.['css'] ||
                                  pageSpeedData.desktop?.pageSize?.resourceBreakdown?.['CSS'] || 0
                                )}</div>
                            </div>
                            <div style="
                                padding: 0.5rem 0.75rem;
                                border-radius: 0.75rem;
                                background-color: ${designSystem.colors.secondary}15;
                            ">
                                <div style="color: ${designSystem.colors.secondary}; font-weight: 600;">스크립트:</div>
                                <div style="color: ${designSystem.colors.textPrimary}; font-weight: 600;">${formatBytes(
                                  pageSpeedData.desktop?.pageSize?.resourceBreakdown?.['script'] ||
                                  pageSpeedData.desktop?.pageSize?.resourceBreakdown?.['Script'] ||
                                  pageSpeedData.desktop?.pageSize?.resourceBreakdown?.['javascript'] ||
                                  pageSpeedData.desktop?.pageSize?.resourceBreakdown?.['JavaScript'] || 0
                                )}</div>
                            </div>
                            <div style="
                                padding: 0.5rem 0.75rem;
                                border-radius: 0.75rem;
                                background-color: ${designSystem.colors.secondary}15;
                            ">
                                <div style="color: ${designSystem.colors.secondary}; font-weight: 600;">이미지:</div>
                                <div style="color: ${designSystem.colors.textPrimary}; font-weight: 600;">${formatBytes(
                                  pageSpeedData.desktop?.pageSize?.resourceBreakdown?.['image'] ||
                                  pageSpeedData.desktop?.pageSize?.resourceBreakdown?.['Image'] || 0
                                )}</div>
                            </div>
                        </div>
                    </div>
                    <!-- 모바일 -->
                    <div>
                        <div style="
                            font-size: 2rem;
                            font-weight: 700;
                            color: ${designSystem.colors.secondary};
                            margin-bottom: 1rem;
                        ">
                            ${formatBytes(pageSpeedData.mobile?.pageSize?.totalSize || 0)}
                            <span style="
                                font-size: 0.875rem;
                                font-weight: 500;
                                color: ${designSystem.colors.textSecondary};
                                margin-left: 0.5rem;
                            ">(모바일)</span>
                        </div>
                        <div style="
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            gap: 0.5rem;
                            font-size: 0.875rem;
                        ">
                            <div style="
                                padding: 0.5rem 0.75rem;
                                border-radius: 0.75rem;
                                background-color: ${designSystem.colors.secondary}15;
                            ">
                                <div style="color: ${designSystem.colors.secondary}; font-weight: 600;">리소스:</div>
                                <div style="color: ${designSystem.colors.textPrimary}; font-weight: 600;">${pageSpeedData.mobile?.pageSize?.resourceCount || 0}개</div>
                            </div>
                            <div style="
                                padding: 0.5rem 0.75rem;
                                border-radius: 0.75rem;
                                background-color: ${designSystem.colors.secondary}15;
                            ">
                                <div style="color: ${designSystem.colors.secondary}; font-weight: 600;">CSS:</div>
                                <div style="color: ${designSystem.colors.textPrimary}; font-weight: 600;">${formatBytes(
                                  pageSpeedData.mobile?.pageSize?.resourceBreakdown?.['stylesheet'] ||
                                  pageSpeedData.mobile?.pageSize?.resourceBreakdown?.['Stylesheet'] ||
                                  pageSpeedData.mobile?.pageSize?.resourceBreakdown?.['css'] ||
                                  pageSpeedData.mobile?.pageSize?.resourceBreakdown?.['CSS'] || 0
                                )}</div>
                            </div>
                            <div style="
                                padding: 0.5rem 0.75rem;
                                border-radius: 0.75rem;
                                background-color: ${designSystem.colors.secondary}15;
                            ">
                                <div style="color: ${designSystem.colors.secondary}; font-weight: 600;">스크립트:</div>
                                <div style="color: ${designSystem.colors.textPrimary}; font-weight: 600;">${formatBytes(
                                  pageSpeedData.mobile?.pageSize?.resourceBreakdown?.['script'] ||
                                  pageSpeedData.mobile?.pageSize?.resourceBreakdown?.['Script'] ||
                                  pageSpeedData.mobile?.pageSize?.resourceBreakdown?.['javascript'] ||
                                  pageSpeedData.mobile?.pageSize?.resourceBreakdown?.['JavaScript'] || 0
                                )}</div>
                            </div>
                            <div style="
                                padding: 0.5rem 0.75rem;
                                border-radius: 0.75rem;
                                background-color: ${designSystem.colors.secondary}15;
                            ">
                                <div style="color: ${designSystem.colors.secondary}; font-weight: 600;">이미지:</div>
                                <div style="color: ${designSystem.colors.textPrimary}; font-weight: 600;">${formatBytes(
                                  pageSpeedData.mobile?.pageSize?.resourceBreakdown?.['image'] ||
                                  pageSpeedData.mobile?.pageSize?.resourceBreakdown?.['Image'] || 0
                                )}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 4. 총 차단 시간 -->
            <div style="
                background: white;
                border-radius: 2rem;
                padding: 2.5rem;
                box-shadow: ${designSystem.shadows.lg};
                border: 1px solid ${designSystem.colors.border};
                text-align: center;
            ">
                <div style="
                    display: inline-block;
                    padding: 0.75rem 1.5rem;
                    border-radius: 1rem;
                    background-color: ${designSystem.colors.warning}20;
                    margin-bottom: 1.5rem;
                ">
                    <h3 style="
                        font-size: 1.875rem;
                        font-weight: 600;
                        color: ${designSystem.colors.warning};
                        margin: 0;
                    ">총 차단 시간</h3>
                </div>
                <div style="display: flex; flex-direction: column; gap: 1.5rem;">
                    <!-- 데스크톱 -->
                    <div style="
                        padding-bottom: 1.5rem;
                        border-bottom: 1px solid ${designSystem.colors.border};
                    ">
                        <div style="
                            font-size: 2rem;
                            font-weight: 700;
                            color: ${designSystem.colors.warning};
                        ">
                            ${Math.round(pageSpeedData.desktop?.totalBlockingTime || 0)}ms
                            <span style="
                                font-size: 0.875rem;
                                font-weight: 500;
                                color: ${designSystem.colors.textSecondary};
                                margin-left: 0.5rem;
                            ">(데스크톱)</span>
                        </div>
                    </div>
                    <!-- 모바일 -->
                    <div>
                        <div style="
                            font-size: 2rem;
                            font-weight: 700;
                            color: ${designSystem.colors.warning};
                        ">
                            ${Math.round(pageSpeedData.mobile?.totalBlockingTime || 0)}ms
                            <span style="
                                font-size: 0.875rem;
                                font-weight: 500;
                                color: ${designSystem.colors.textSecondary};
                                margin-left: 0.5rem;
                            ">(모바일)</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- AI 분석 섹션 -->
        ${llmAnalysis ? `
        <div style="
            background: white;
            border-radius: 2rem;
            padding: 3rem;
            box-shadow: ${designSystem.shadows.lg};
            border: 1px solid ${designSystem.colors.border};
            margin-bottom: 4rem;
        ">
            <h2 style="
                font-size: 2.25rem;
                font-weight: 700;
                color: ${designSystem.colors.primary};
                margin-bottom: 2rem;
                text-align: center;
            ">분석 총평</h2>
            <div style="
                font-size: 1rem;
                line-height: 1.8;
                color: ${designSystem.colors.textPrimary};
            ">
                ${generateLlmHtml(llmAnalysis)}
            </div>
        </div>
        ` : ''}

        <!-- 개선 권장사항 섹션 -->
        ${(desktopOpportunities.length > 0 || mobileOpportunities.length > 0) ? `
        <div style="margin-bottom: 4rem;">
            <h2 style="
                font-size: 2.25rem;
                font-weight: 700;
                color: ${designSystem.colors.primary};
                text-align: center;
                margin-bottom: 2rem;
            ">핵심 성능 개선 기회</h2>

            ${desktopOpportunities.length > 0 ? `
            <div style="margin-bottom: 3rem;">
                <h3 style="
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: ${designSystem.colors.success};
                    margin-bottom: 1.5rem;
                    padding: 1rem 1.5rem;
                    background: ${designSystem.colors.success}15;
                    border-radius: 1rem;
                ">🖥️ 데스크톱 (${desktopOpportunities.length}개)</h3>
                ${desktopOpportunities.slice(0, 5).map((opp, index) => `
                    <div style="
                        background: white;
                        border-radius: 1.5rem;
                        padding: 2rem;
                        box-shadow: ${designSystem.shadows.lg};
                        border: 2px solid ${designSystem.colors.success}30;
                        margin-bottom: 1.5rem;
                    ">
                        <h4 style="
                            font-size: 1.25rem;
                            font-weight: 600;
                            color: ${designSystem.colors.success};
                            margin-bottom: 1rem;
                        ">${translateOpportunityTitle(opp.title)}</h4>
                        <p style="
                            color: ${designSystem.colors.textSecondary};
                            line-height: 1.6;
                            margin-bottom: ${opp.details?.items?.length > 0 ? '1rem' : '0'};
                        ">${translateDescription(opp.description)}</p>
                        ${opp.details?.items?.length > 0 ? `
                            <div style="
                                margin-top: 1rem;
                                padding: 1rem;
                                background: ${designSystem.colors.bgPrimary};
                                border-radius: 0.75rem;
                            ">
                                <div style="
                                    font-weight: 600;
                                    color: ${designSystem.colors.textSecondary};
                                    margin-bottom: 0.75rem;
                                ">관련 리소스 (${opp.details.items.length}개)</div>
                                ${opp.details.items.slice(0, 5).map((item: any) => `
                                    <div class="resource-item" style="
                                        font-family: 'Consolas', 'Monaco', monospace;
                                        font-size: 0.8125rem;
                                        color: ${designSystem.colors.primary};
                                        padding: 0.5rem 0.75rem;
                                        background: white;
                                        border-radius: 0.5rem;
                                        margin-bottom: 0.5rem;
                                        word-break: break-all;
                                    ">${item.url || item.source || '알 수 없음'}</div>
                                `).join('')}
                                ${opp.details.items.length > 5 ? `
                                    <div id="desktop-opp-${index}-resources" class="hidden-resources">
                                        ${opp.details.items.slice(5).map((item: any) => `
                                            <div class="resource-item" style="
                                                font-family: 'Consolas', 'Monaco', monospace;
                                                font-size: 0.8125rem;
                                                color: ${designSystem.colors.primary};
                                                padding: 0.5rem 0.75rem;
                                                background: white;
                                                border-radius: 0.5rem;
                                                margin-bottom: 0.5rem;
                                                word-break: break-all;
                                            ">${item.url || item.source || '알 수 없음'}</div>
                                        `).join('')}
                                    </div>
                                    <button
                                        id="desktop-opp-${index}-btn"
                                        class="show-more-btn"
                                        onclick="toggleResources('desktop-opp-${index}-btn', 'desktop-opp-${index}-resources')"
                                    >
                                        나머지 ${opp.details.items.length - 5}개 더보기 ▼
                                    </button>
                                ` : ''}
                            </div>
                        ` : ''}
                    </div>
                `).join('')}
            </div>
            ` : ''}

            ${mobileOpportunities.length > 0 ? `
            <div>
                <h3 style="
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: ${designSystem.colors.warning};
                    margin-bottom: 1.5rem;
                    padding: 1rem 1.5rem;
                    background: ${designSystem.colors.warning}15;
                    border-radius: 1rem;
                ">📱 모바일 (${mobileOpportunities.length}개)</h3>
                ${mobileOpportunities.slice(0, 5).map((opp, index) => `
                    <div style="
                        background: white;
                        border-radius: 1.5rem;
                        padding: 2rem;
                        box-shadow: ${designSystem.shadows.lg};
                        border: 2px solid ${designSystem.colors.warning}30;
                        margin-bottom: 1.5rem;
                    ">
                        <h4 style="
                            font-size: 1.25rem;
                            font-weight: 600;
                            color: ${designSystem.colors.warning};
                            margin-bottom: 1rem;
                        ">${translateOpportunityTitle(opp.title)}</h4>
                        <p style="
                            color: ${designSystem.colors.textSecondary};
                            line-height: 1.6;
                            margin-bottom: ${opp.details?.items?.length > 0 ? '1rem' : '0'};
                        ">${translateDescription(opp.description)}</p>
                        ${opp.details?.items?.length > 0 ? `
                            <div style="
                                margin-top: 1rem;
                                padding: 1rem;
                                background: ${designSystem.colors.bgPrimary};
                                border-radius: 0.75rem;
                            ">
                                <div style="
                                    font-weight: 600;
                                    color: ${designSystem.colors.textSecondary};
                                    margin-bottom: 0.75rem;
                                ">관련 리소스 (${opp.details.items.length}개)</div>
                                ${opp.details.items.slice(0, 5).map((item: any) => `
                                    <div class="resource-item" style="
                                        font-family: 'Consolas', 'Monaco', monospace;
                                        font-size: 0.8125rem;
                                        color: ${designSystem.colors.primary};
                                        padding: 0.5rem 0.75rem;
                                        background: white;
                                        border-radius: 0.5rem;
                                        margin-bottom: 0.5rem;
                                        word-break: break-all;
                                    ">${item.url || item.source || '알 수 없음'}</div>
                                `).join('')}
                                ${opp.details.items.length > 5 ? `
                                    <div id="mobile-opp-${index}-resources" class="hidden-resources">
                                        ${opp.details.items.slice(5).map((item: any) => `
                                            <div class="resource-item" style="
                                                font-family: 'Consolas', 'Monaco', monospace;
                                                font-size: 0.8125rem;
                                                color: ${designSystem.colors.primary};
                                                padding: 0.5rem 0.75rem;
                                                background: white;
                                                border-radius: 0.5rem;
                                                margin-bottom: 0.5rem;
                                                word-break: break-all;
                                            ">${item.url || item.source || '알 수 없음'}</div>
                                        `).join('')}
                                    </div>
                                    <button
                                        id="mobile-opp-${index}-btn"
                                        class="show-more-btn"
                                        onclick="toggleResources('mobile-opp-${index}-btn', 'mobile-opp-${index}-resources')"
                                    >
                                        나머지 ${opp.details.items.length - 5}개 더보기 ▼
                                    </button>
                                ` : ''}
                            </div>
                        ` : ''}
                    </div>
                `).join('')}
            </div>
            ` : ''}
        </div>
        ` : ''}

        <!-- 마무리 -->
        <div style="
            text-align: center;
            padding: 2rem;
            color: ${designSystem.colors.textSecondary};
            font-style: italic;
        ">
            <p style="margin: 0;">이 보고서는 ${new Date().toLocaleString('ko-KR')}에 생성되었습니다.</p>
        </div>
    </div>
</body>
</html>
  `;

  return html;
};
